import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Section, DataField } from "../../components/common-components";
import { stripHtmlToPlainText } from "../../../../helpers/interventionHtml";
import { DPTOptions } from "../../../patient/text/data";
import {
    getAmrMedicationRowForIntervention,
    isAnnualMedicationReviewIntervention,
    DocumentationForms,
} from "../../../patient/Patient-Menu/Interventions/data";
import {
    AdherenceTypes,
    AmrDisplayKind,
    formatAmrForDisplay,
    formatAmrAdherenceStatusForDocumentation,
} from "../../../patient/clinical-services/service-steps/medication/data";
import { reasonOptions } from "../../../patient/clinical-services/service-steps/clinical-assessment/data";

export const DetailsSection = ({
    formType,
    interventionDetails = {},
    interventionType = null,
    clinicalServiceName = "",
    clinicalServiceTypeCode = "",
    intervention = null,
}) => {
    const interventionClinicalServiceObj = useSelector((state) => state.intervention?.clinicalServiceObj);
    const clinicalServiceModuleObj = useSelector((state) => state.clinicalService?.clinicalServiceObj);

    const normalizeId = (value) =>
        value == null || value === ""
            ? ""
            : String(value).toLowerCase().replace(/[{}]/g, "").trim();

    const amrMedicationRow = useMemo(() => {
        if (!intervention || !isAnnualMedicationReviewIntervention(intervention)) {
            return null;
        }

        const medicationIdFromIntervention =
            intervention?.medicationId ??
            interventionDetails?.medicationId ??
            intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails?.medicationId;
        const targetMedicationId = normalizeId(medicationIdFromIntervention);

        const pickFromClinicalService = (serviceObj) =>
            getAmrMedicationRowForIntervention(serviceObj, intervention);

        const rowFromInterventionModule = pickFromClinicalService(interventionClinicalServiceObj);
        if (rowFromInterventionModule) return rowFromInterventionModule;

        const rowFromClinicalServiceModule = pickFromClinicalService(clinicalServiceModuleObj);
        if (rowFromClinicalServiceModule) return rowFromClinicalServiceModule;

        const formRows =
            intervention?.interventionAssessment?.interventionAssessmentForm?.medications?.medications;
        if (!Array.isArray(formRows) || formRows.length === 0 || !targetMedicationId) {
            return null;
        }

        return (
            formRows.find((row) => {
                const rowId = normalizeId(row?.medicationId ?? row?.MedicationId ?? row?.id ?? row?.Id);
                return rowId === targetMedicationId;
            }) || null
        );
    }, [interventionClinicalServiceObj, clinicalServiceModuleObj, intervention, interventionDetails?.medicationId]);

    const amrAdherenceForAssessmentHeading =
        amrMedicationRow?.adherence ??
        amrMedicationRow?.Adherence ??
        amrMedicationRow?.consumption ??
        amrMedicationRow?.Consumption ??
        amrMedicationRow?.adherenceStatus ??
        amrMedicationRow?.AdherenceStatus ??
        interventionDetails?.adherence ??
        interventionDetails?.Adherence ??
        interventionDetails?.consumption ??
        interventionDetails?.Consumption ??
        intervention?.adherence ??
        intervention?.Adherence ??
        intervention?.consumption ??
        intervention?.Consumption ??
        null;

    const showAmrAssessmentBlock =
        intervention != null && isAnnualMedicationReviewIntervention(intervention);

    const {
        drugTherapyProblem,
        actionPlan = [],
        drugTherapySuggestions,
        comments,
        pharmacistComments,
        medicationName,
        route,
        dosage,
        form: medForm,
        frequency,
        whoChangedMedication,
        whyChangedMedication,
        whenChangedMedication,
        reason: interventionReason,
    } = interventionDetails || {};

    const getFirstNonEmptyText = (...values) => {
        for (const value of values) {
            const text = String(value ?? "");
            if (text.trim().length > 0) {
                return text;
            }
        }
        return "";
    };

    const getReasonTakingLabel = (reasonValue) => {
        if (reasonValue == null || reasonValue === "") return "";
        const opt = reasonOptions.find((r) => r.value === reasonValue);
        return opt?.label || String(reasonValue);
    };

    const reasonTakingRaw = getFirstNonEmptyText(
        interventionReason,
        interventionDetails?.Reason,
        amrMedicationRow?.reason,
        amrMedicationRow?.Reason
    );
    const reasonTakingDisplay = getReasonTakingLabel(reasonTakingRaw);

    const adherenceStatusDisplay = formatAmrAdherenceStatusForDocumentation(amrAdherenceForAssessmentHeading);

    /** Always show assessment comments section; fallback text when empty. */
    const documentationPharmacistAssessmentComments =
        getFirstNonEmptyText(drugTherapySuggestions, interventionDetails?.DrugTherapySuggestions);

    const pharmacistAssessmentCommentsStr = stripHtmlToPlainText(documentationPharmacistAssessmentComments ?? "").trim();
    const pharmacistAssessmentCommentsDisplay =
        pharmacistAssessmentCommentsStr.length > 0
            ? stripHtmlToPlainText(String(documentationPharmacistAssessmentComments ?? ""))
            : "No additional comments added";

    /** Always show pharmacist comments section; fallback text when empty. */
    const documentationPharmacistComments =
        getFirstNonEmptyText(
            comments,
            pharmacistComments,
            interventionDetails?.Comments,
            interventionDetails?.PharmacistComments
        );

    const pharmacistCommentsStr = stripHtmlToPlainText(documentationPharmacistComments ?? "").trim();
    const pharmacistCommentsDisplay =
        pharmacistCommentsStr.length > 0
            ? stripHtmlToPlainText(String(documentationPharmacistComments ?? ""))
            : "No additional comments added";

    const currentRegimen = `${medicationName || ""} ${route || ""} ${dosage || ""} ${medForm || ""}`.trim();
    const originalPrescription = medicationName ? `${medicationName} ${dosage || ""}`.trim() : "";
    const actionPlanDisplay = (Array.isArray(actionPlan) && actionPlan.length > 0) ? (actionPlan[0] || actionPlan.join(", ")) : "";

    // Map internal DTP value to the shared label from DPTOptions (consistent with InterventionCreate)
    const dtpOption = DPTOptions.find((o) => o.value === drugTherapyProblem);
    const drugTherapyProblemLabel = dtpOption ? dtpOption.label : (drugTherapyProblem || "");

    const urgencyLabel = interventionDetails?.urgencyLevel ? String(interventionDetails.urgencyLevel) : "";

    const interventionTypeLabel = (() => {
        if (!interventionType) return "";
        const match = DocumentationForms.find((f) => f.type === interventionType);
        return match ? match.name : interventionType;
    })();

    const hideTherapyChangeFields = !isAnnualMedicationReviewIntervention({
        clinicalServiceType: clinicalServiceName,
        clinicalServiceTypeCode,
    });

    return (
        <Section formType={formType}>
            <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-teal-300">
                    <DataField
                        label="Intervention Type:"
                        value={interventionTypeLabel}
                        className="!text-sm"
                    />
                    <DataField
                        label="Urgency Level:"
                        value={urgencyLabel ? urgencyLabel.charAt(0).toUpperCase() + urgencyLabel.slice(1) : ""}
                        className="pt-1"
                    />

                    <DataField label="Drug Therapy Problem (DTP)" value={drugTherapyProblemLabel} vertical={true} />

                    <DataField label="Action Plan" value={actionPlanDisplay} />
                    <div className="col-span-2 border-t border-gray-100 mt-1"></div>
                    <DataField
                        label="Pharmacist Intervention Comments:"
                        value={pharmacistCommentsDisplay}
                        className="col-span-2 text-sm text-teal-300 [&_dd]:font-semibold [&_dd]:whitespace-pre-wrap [&_dd]:break-words"
                    />
                </div>

                <div className="bg-gray-10 border-t border-gray-100 -mx-4">
                    <div className="flex w-full items-stretch">
                        <div className="flex-1 min-w-0 py-2 flex flex-col">
                            <div className="px-4 flex flex-col flex-1 min-h-0">
                                <div className="space-y-2 flex-1">
                                    <DataField
                                        label="Original Prescription:"
                                        vertical={true}
                                        value={originalPrescription || ""}
                                    />
                                    <div className="border-t border-gray-100"></div>
                                    {showAmrAssessmentBlock && (
                                        <div className="space-y-1">
                                            <p className="font-semibold text-xs text-gray-800">
                                                Assessment Results
                                            </p>
                                            <DataField
                                                label="Medication Effectiveness:"
                                                value={formatAmrForDisplay(
                                                    AmrDisplayKind.CommonQuestion,
                                                    amrMedicationRow?.effectiveness ??
                                                        amrMedicationRow?.Effectiveness ?? amrMedicationRow?.working ?? amrMedicationRow?.Working
                                                )}
                                            />
                                            <DataField
                                                label="Safety/ Side Effects:"
                                                value={formatAmrForDisplay(
                                                    AmrDisplayKind.CommonQuestion,
                                                    amrMedicationRow?.safety ?? amrMedicationRow?.Safety ?? amrMedicationRow?.safe ?? amrMedicationRow?.Safe
                                                )}
                                            />
                                            <DataField
                                                label="Appropriate Technique to Use:"
                                                value={formatAmrForDisplay(
                                                    AmrDisplayKind.CommonQuestion,
                                                    amrMedicationRow?.properTechnique ?? amrMedicationRow?.ProperTechnique ?? amrMedicationRow?.technique ?? amrMedicationRow?.Technique,
                                                    { forTechnique: true }
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="pt-2 mt-auto">
                                    <DataField label="Reason Taking:" value={reasonTakingDisplay} />
                                </div>
                            </div>
                        </div>

                        <div
                            className="w-px shrink-0 self-stretch bg-gray-200 my-1"
                            aria-hidden="true"
                        />

                        <div className="flex-1 min-w-0 py-2 flex flex-col">
                            <div className="px-4 flex flex-col flex-1 min-h-0">
                                <div className="space-y-2 flex-1">
                                    <DataField
                                        label="Currently Taking:"
                                        vertical={true}
                                        value={currentRegimen || ""}
                                        noWrapLabel={true}
                                    />
                                    <div className="border-t border-gray-100"></div>

                                    {!hideTherapyChangeFields && (
                                        <div className="space-y-1">
                                            <p className="font-semibold text-xs text-gray-800">
                                                Medication Change Summary
                                            </p>
                                            <DataField
                                                label="Who changed the therapy?"
                                                value={whoChangedMedication || ""}
                                            />

                                            <DataField
                                                label="Why was the therapy changed?"
                                                value={whyChangedMedication || ""}
                                            />

                                            <DataField
                                                label="When was the therapy changed?"
                                                value={whenChangedMedication || ""}
                                            />
                                        </div>
                                    )}
                                </div>
                                {showAmrAssessmentBlock && (
                                    <div className="pt-2 mt-auto text-xs text-gray-800">
                                        <p className="leading-normal">
                                            <span className="font-semibold">Adherence Status: </span>
                                            <span className="font-normal">{adherenceStatusDisplay}</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-100 rounded mx-4 mb-0 px-2 py-2">
                        <p className="text-xs text-gray-800 leading-normal whitespace-pre-wrap break-words">
                            <span className="font-semibold">Pharmacist Assessment Comments:</span>
                            <span className="font-normal"> {pharmacistAssessmentCommentsDisplay}</span>
                        </p>
                    </div>
                </div>
            </div>
        </Section>
    )
}